// The Certify HTTP wire contract (ADR-D46 decisions-adr.md:105; slice-plan.md:60-68).
//
// The SINGLE source of truth for the Certify routes that apps/api (producer) and apps/web (consumer)
// build against - pinned here so the two sides cannot drift (the Slice-2 wire-drift lesson). Success
// payloads are the domain schemas from ../model/certify.js; this file adds the path params, request
// bodies, and the route path strings the api lane consumes verbatim. Every failure returns the shared
// `ErrorEnvelope { code, message }` (http/knowledge.ts:21) - one shape both sides narrow, reused here,
// not redefined; a `not_found` and a `forbidden` on an app that is not the caller's return a
// byte-identical not-found (app-security §2 A01 - existence is not revealed).
import { z } from "zod";
import { AppId } from "../ids.js";
import { NonEmptyString, Timestamp } from "../model/common.js";
import { Assessment, BuilderHealthCheck, Certificate, FrameworkKey, ScanResult, TrustPageView, } from "../model/certify.js";
/**
 * The route path strings, for the api lane to register verbatim. App-scoped routes follow the
 * existing `/v1/apps/:appId/...` idiom (http/knowledge.ts). The public trust page is a DISTINCT,
 * unauthenticated surface behind the `certify.public_trust_page` release flag (default OFF, fail
 * closed) - it is keyed by a url-safe slug, never by the internal app UUID.
 */
export const CERTIFY_ROUTES = {
    scan: "/v1/apps/:appId/certify/scan",
    assess: "/v1/apps/:appId/certify/assess",
    certificate: "/v1/apps/:appId/certify/certificate",
    health: "/v1/apps/:appId/certify/health",
    trustPagePublic: "/v1/trust/:slug",
};
/** Path params for the app-scoped Certify routes: the one app the request is scoped to. */
export const CertifyRouteParams = z.object({ appId: AppId });
// --- CertifyScan: POST /v1/apps/:appId/certify/scan ---
/** Run the deterministic scanners. `scanners` narrows the run; absent = every scanner. */
export const CertifyScanRequest = z.object({
    scanners: z.array(NonEmptyString).optional(),
});
/** 200 -> the per-scanner results, else an `ErrorEnvelope`. */
export const CertifyScanResponse = z.object({ results: z.array(ScanResult) });
// --- CertifyAssess: POST /v1/apps/:appId/certify/assess ---
/** Assess the app against one selected framework (D46; the GDPR baseline plus opt-in frameworks). */
export const CertifyAssessRequest = z.object({ framework: FrameworkKey });
/** 200 -> the `Assessment` (always labeled a self-assessment by construction, D46), else an `ErrorEnvelope`. */
export const CertifyAssessResponse = Assessment;
// --- CertificateUpload: POST /v1/apps/:appId/certify/certificate ---
/**
 * The founder-uploaded certificate's metadata. The file itself arrives multipart at the edge, is
 * scanned and stored PRIVATELY (app-security §1; the hardened upload gate, slice-plan.md:76), and its
 * `fileRef` + `verifyState` are assigned server-side - so they are NOT part of the request body.
 */
export const CertificateUploadRequest = z
    .object({
    issuer: NonEmptyString,
    scope: NonEmptyString,
    issuedAt: Timestamp,
    expiresAt: Timestamp,
})
    // Date-coherence guard at the REQUEST boundary (mirrors `Certificate` ~model/certify.ts:186), so an
    // incoherent validity window is rejected 400 at the controller/service parse BEFORE any storage.put
    // - not stored-then-tripped by the DB constraint (pentest F1: a 500 + an orphaned stored object).
    .refine((v) => Date.parse(v.expiresAt) > Date.parse(v.issuedAt), {
    message: "expiresAt must be after issuedAt",
    path: ["expiresAt"],
});
/** 200 -> the stored `Certificate` (founderUploaded is true by construction, D46), else an `ErrorEnvelope`. */
export const CertificateUploadResponse = Certificate;
// --- Builder health: GET /v1/apps/:appId/certify/health ---
/** 200 -> the `BuilderHealthCheck` bundle (email deliverability, domain/SSL, payments), else an `ErrorEnvelope`. */
export const CertifyHealthResponse = BuilderHealthCheck;
// --- TrustPagePublicView: GET /v1/trust/:slug (public, flag-gated) ---
/** A url-safe public trust-page slug. Bounded, lowercase; untrusted public input, parsed at the edge. */
export const TrustPageSlug = z
    .string()
    .trim()
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, "must be a url-safe slug")
    .brand();
/** Path params for the public trust page: the slug, never the internal app UUID. */
export const TrustPagePublicParams = z.object({ slug: TrustPageSlug });
/** 200 -> the public `TrustPageView` (self-assessment + any verified credential, distinct), else an `ErrorEnvelope`. */
export const TrustPagePublicView = TrustPageView;
//# sourceMappingURL=certify.js.map